import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  CheckCircle as SuccessIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import unifiedApi from '../../utils/unifiedApi';
import appConfig from '../../config/appConfig';

const EventRegistrationCallbackPage = () => {
  const { eventSlug } = useParams();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('processing');
  const [result, setResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);

  useEffect(() => {
    const confirmPayment = async () => {
      const transactionId = searchParams.get('transactionId');
      const orderId = searchParams.get('orderId');
      const sessionToken = localStorage.getItem('eventRegistrationSessionToken');

      if (!sessionToken) {
        setStatus('error');
        setErrorMessage('Registration session not found. Please try registering again.');
        return;
      }

      try {
        const payload = { sessionToken };
        if (transactionId) payload.transactionId = transactionId;
        if (orderId) payload.orderId = orderId;

        const data = await unifiedApi.confirmEventRegistration(payload);

        if (data?.success) {
          setResult(data);
          setStatus('success');
          setEmailDialogOpen(true);
          localStorage.removeItem('eventRegistrationSessionToken');
        } else {
          setStatus('error');
          setErrorMessage(data?.message || 'Registration confirmation failed.');
        }
      } catch (err) {
        setStatus('error');
        setErrorMessage(err?.message || 'Failed to confirm registration. Please contact the administrator.');
        console.error('Event registration confirmation error:', err);
      }
    };

    confirmPayment();
  }, [searchParams]);

  return (
    <Box display="flex" justifyContent="center" mt={4} px={2}>
      <Paper sx={{ p: 4, maxWidth: 600, width: '100%', textAlign: 'center' }}>
        {status === 'processing' && (
          <>
            <CircularProgress size={48} sx={{ mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Confirming Your Registration
            </Typography>
            <Typography color="text.secondary">
              Please wait while we verify your payment...
            </Typography>
          </>
        )}

        {status === 'success' && result && (
          <Stack spacing={2} alignItems="center">
            <SuccessIcon sx={{ fontSize: 64, color: 'success.main' }} />
            <Typography variant="h5">
              Registration Confirmed!
            </Typography>
            <Typography color="text.secondary">
              Welcome, {result.firstName}! Your registration for {result.eventName} has been confirmed.
            </Typography>
            <Alert severity="info" sx={{ textAlign: 'left', width: '100%' }}>
              A confirmation email has been sent to <strong>{result.email}</strong>.
            </Alert>
            <Button
              component={Link}
              to="/register"
              variant="contained"
              sx={{ mt: 2 }}
            >
              Back to Registration
            </Button>
          </Stack>
        )}

        {status === 'error' && (
          <Stack spacing={2} alignItems="center">
            <ErrorIcon sx={{ fontSize: 64, color: 'error.main' }} />
            <Typography variant="h5">
              Something Went Wrong
            </Typography>
            <Alert severity="error" sx={{ textAlign: 'left', width: '100%' }}>
              {errorMessage}
            </Alert>
            <Button
              component={Link}
              to={`/events/${eventSlug}/register`}
              variant="outlined"
              sx={{ mt: 2 }}
            >
              Try Again
            </Button>
          </Stack>
        )}
      </Paper>

      <Dialog
        open={emailDialogOpen}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown
        onClose={(_event, reason) => {
          if (reason !== 'backdropClick') setEmailDialogOpen(false);
        }}
      >
        <DialogTitle>Registration Successful!</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography>
              Your registration has been confirmed. A confirmation email has been sent.
            </Typography>
            <Alert severity="warning" sx={{ textAlign: 'left' }}>
              <Typography variant="body2" gutterBottom>
                <strong>Please check your junk/spam folder</strong> if you don&apos;t see the
                confirmation email in your inbox.
              </Typography>
              <Typography variant="body2" gutterBottom>
                The email was sent from: <strong>{appConfig.registrationSenderEmail}</strong>
              </Typography>
              <Typography variant="body2">
                The email was sent to: <strong>{result?.email}</strong>
              </Typography>
            </Alert>
            <Typography variant="body2" color="text.secondary">
              You may want to add <strong>{appConfig.registrationSenderEmail}</strong> to your
              contacts or safe sender list to ensure future emails are delivered to your inbox.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailDialogOpen(false)} variant="contained">
            Got It
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EventRegistrationCallbackPage;
